/* Presentation rules for the Suggested rules screen. Pure — no React, no fetch.
 *
 * **Every judgement here is transcribed from the Claude Design reference's own script**, screen 36
 * of `Lumiq App.dc.html`. Two of them are behavioural rather than cosmetic and are the reason this
 * file exists rather than the screen deciding inline:
 *
 * 1. **The AI status is the authority, not the list.** The reference derives its model rows from
 *    the status — `sgAiState === 'contributed' ? all : all.filter(x => x.tier !== 'ai')` — with the
 *    comment that filtering by tier alone "would let a run reporting a rejected answer still show
 *    model rows if one survived serialisation". Our API can produce exactly that: `model_status`
 *    and the stored list are written by different statements, and a regeneration that fails after
 *    storing leaves the previous run's rows readable. So the screen must never show an AI row on a
 *    run whose status is not `contributed`.
 * 2. **`not_configured` absorbs the empty case.** `nothing_to_ask` with no deterministic
 *    suggestions is reported as not-configured, because "the deterministic pass already covered
 *    everything" is a contradiction when it covered nothing.
 *
 * A previous version of this file composed an English claim sentence per suggestion
 * (`claimOf`). That came from an *older* export of the design — `standalone-src.dc.html`, 38
 * screens against 43 — whose Suggestions screen is a list of claims. The authoritative screen is a
 * table whose first cell is the raw `kind` in the mono face, so there is no sentence to compose and
 * the function is gone rather than kept for a caller that no longer exists.
 */

import type {
  CandidateSource,
  GenerationOutcome,
  ModelStatus,
  Suggestion,
} from '../api/rulegen';

/** The three evidence tiers, in the reference's own order: strongest first. */
export const TIER_ORDER: readonly CandidateSource[] = [
  'schema_declared',
  'schema_inferred',
  'model_proposed',
];

/** How a tier is presented: its i18n key stem and its colour modifier.
 *
 * `satisfies Record<CandidateSource, …>` makes a fourth source a compile error rather than an
 * unlabelled card — the total-map discipline this codebase adopted after a `switch` with a
 * `default:` rendered an unknown verdict in the *permitted* tone.
 */
const TIER = {
  schema_declared: { key: 'suggestions.source.declared', modifier: 'declared' },
  schema_inferred: { key: 'suggestions.source.inferred', modifier: 'inferred' },
  model_proposed: { key: 'suggestions.source.model', modifier: 'ai' },
} satisfies Record<CandidateSource, { readonly key: string; readonly modifier: string }>;

/** The i18n key stem for a tier's title, badge, subtitle and meaning.
 *
 * An unrecognised source is presented as the model tier, which is the same answer every sibling
 * gives it: the tier that asks a reviewer to scrutinise hardest. Reachable because the generated
 * envelope guard checks `Array.isArray` and does not run the item guard, so a fourth source from a
 * server one version ahead arrives here with the type still claiming it cannot.
 */
export function tierKey(source: CandidateSource): string {
  const table: Readonly<Record<string, { readonly key: string } | undefined>> = TIER;
  return (table[source] ?? TIER.model_proposed).key;
}

/** The tier's colour modifier suffix, for `.lq-sg__tier--*` and `.lq-sg__chip--*`. */
export function tierModifier(source: CandidateSource): string {
  const table: Readonly<Record<string, { readonly modifier: string } | undefined>> = TIER;
  return (table[source] ?? TIER.model_proposed).modifier;
}

/** Whether a tier's chip carries a filled dot or a hollow ring.
 *
 * The reference fills the dot for declared and inferred and leaves the AI one transparent — so
 * "a model proposed this" is distinguishable from an enforced fact without relying on hue, which
 * is what makes the distinction survive greyscale.
 */
export function tierDotIsFilled(source: CandidateSource): boolean {
  return source !== 'model_proposed';
}

/** How an AI status is presented. Six states, one per `ModelStatus` member. */
const AI = {
  contributed: { tone: 'ok', filled: true },
  not_configured: { tone: 'unknown', filled: false },
  no_route: { tone: 'unknown', filled: false },
  unavailable: { tone: 'warn', filled: true },
  rejected: { tone: 'warn', filled: true },
  nothing_to_ask: { tone: 'unknown', filled: false },
} satisfies Record<ModelStatus, { readonly tone: string; readonly filled: boolean }>;

export interface AiPresentation {
  /** `suggestions.ai.<status>`; `.note` appended gives the sentence beneath it. */
  readonly key: string;
  /** `ok` | `warn` | `unknown`, for `.lq-sg__chip--*` and the tile's top rule. */
  readonly tone: string;
  readonly filled: boolean;
}

/** The status a run should be *presented* as, which is not always the status it reports.
 *
 * `nothing_to_ask` with nothing deterministic to show becomes `not_configured`, because "the
 * deterministic pass already covered every column worth suggesting" is a false sentence on a run
 * that produced no suggestion at all. The reference derives this rather than trusting its seed,
 * with the note that the contradiction is derivable and a future dataset would reintroduce it.
 */
export function presentedStatus(
  status: ModelStatus,
  deterministicCount: number,
): ModelStatus {
  return status === 'nothing_to_ask' && deterministicCount === 0
    ? 'not_configured'
    : status;
}

export function aiPresentation(status: ModelStatus): AiPresentation {
  const table: Readonly<
    Record<string, { readonly tone: string; readonly filled: boolean } | undefined>
  > = AI;
  const found = table[status] ?? AI.not_configured;
  return { key: `suggestions.ai.${status}`, tone: found.tone, filled: found.filled };
}

/** The suggestions a run may show. **Filtered by status, never by tier alone.**
 *
 * This is the reference's invariant and the screen's central honesty property: a model row appears
 * only when the run's status is `contributed`. Our two writes are separate — the stored suggestion
 * rows and the outcome's status come from different statements — so a run that stored a model
 * candidate and then reported `rejected` is representable, and filtering on `source` alone would
 * render "AI Suggested" for a run whose own status says nothing from the model was accepted.
 */
export function visibleSuggestions(
  suggestions: readonly Suggestion[],
  status: ModelStatus,
): readonly Suggestion[] {
  if (status === 'contributed') return suggestions;
  return suggestions.filter((item) => item.source !== 'model_proposed');
}

/** Suggestions grouped by tier, strongest first, omitting a tier with nothing in it.
 *
 * An empty tier is left out rather than shown empty: an "AI Suggested" heading over no rows reads
 * as a failure, and the reference puts the status chip and its sentence there instead — see
 * `showsAiStandIn`.
 */
export function groupedByTier(suggestions: readonly Suggestion[]): readonly {
  readonly source: CandidateSource;
  readonly items: readonly Suggestion[];
}[] {
  const known = new Set<string>(TIER_ORDER);
  /* A source the union does not contain is grouped with the model tier rather than dropped. The
     first version of this filtered on the three literals and lost anything else, while `tierKey`
     and `tierModifier` gave an unknown source the model answer — three functions disagreeing about
     one unknown in one file. */
  const unknown = suggestions.filter((item) => !known.has(item.source));
  return TIER_ORDER.map((source) => ({
    source,
    items:
      source === 'model_proposed'
        ? [...suggestions.filter((item) => item.source === source), ...unknown]
        : suggestions.filter((item) => item.source === source),
  })).filter((group) => group.items.length > 0);
}

/** Whether the status chip stands in for an absent AI group.
 *
 * True when there is something to show and no model contributed — so the reader is told why there
 * is no AI tier rather than being left to notice its absence. False on an empty run, where the
 * empty state already carries the explanation.
 */
export function showsAiStandIn(status: ModelStatus, visibleCount: number): boolean {
  return status !== 'contributed' && visibleCount > 0;
}

/** One of the four figures above the tiers. */
export interface Tile {
  readonly value: string;
  /** i18n key for the uppercase label. */
  readonly label: string;
  /** i18n key for the sentence beneath it. */
  readonly hint: string;
  /** Interpolation for `hint`, when it takes any. */
  readonly hintValues?: Readonly<Record<string, number>>;
  /** `''` for ink, or a `.lq-sg__tile-value--*` suffix. */
  readonly modifier: string;
}

/** Past a dozen, a refusal count stops being the validator working. The reference's threshold. */
export const REJECTED_ATTENTION = 12;

/** The reference's four tiles, computed from one run's outcome and the rows it may show.
 *
 * `visibleCount` rather than `outcome.accepted`, because the status filter above can hide model
 * rows the run did accept — and a headline figure that counted rows the table does not show would
 * be a contradiction on the same screen.
 */
export function tilesFor(
  outcome: GenerationOutcome,
  visible: readonly Suggestion[],
): readonly Tile[] {
  const total = visible.length;
  const withoutModel = visible.filter((item) => item.source !== 'model_proposed').length;
  const withModel = total - withoutModel;
  const refused = totalRejected(outcome.rejected);

  return [
    {
      value: String(outcome.columns_considered),
      label: 'suggestions.tile.columns',
      hint: 'suggestions.tile.columns.hint',
      modifier: '',
    },
    {
      value: String(total),
      label: 'suggestions.tile.total',
      hint:
        total === 0
          ? 'suggestions.tile.total.none'
          : withModel > 0
            ? 'suggestions.tile.total.tiers'
            : 'suggestions.tile.total.deterministic',
      modifier: '',
    },
    {
      /* **A dash, never 0%.** Zero percent is a claim about the generator; an empty run is a claim
         about nothing, and 0% would read as "a model did all the work" on a run that produced
         none. The reference's own reasoning, in its own words. */
      value: total === 0 ? '—' : `${String(Math.round((withoutModel / total) * 100))}%`,
      label: 'suggestions.tile.share',
      hint: total === 0 ? 'suggestions.tile.share.none' : 'suggestions.tile.share.hint',
      hintValues: { withoutModel, total },
      modifier: '',
    },
    {
      value: String(refused),
      label: 'suggestions.tile.rejected',
      hint:
        refused === 0
          ? 'suggestions.tile.rejected.none'
          : 'suggestions.tile.rejected.hint',
      /* Neutral, not red. A candidate naming a column that is not on this dataset is the
         validator working, not an incident — so only an unusually high count takes amber. */
      modifier: refused > REJECTED_ATTENTION ? 'warn' : refused === 0 ? 'quiet' : 'quiet',
    },
  ];
}

/** How many candidates were refused, in total. `0` is a real and common answer.
 *
 * **Counts only, and the contract is why: a rejection detail can name a column, and a rejected
 * *model* candidate is the one place a column name may not be one we sent.** So there is
 * deliberately no function here returning a rejected candidate — there is nothing in the payload
 * to return, and the reference's own tile hint says "counted by reason, never shown".
 */
export function totalRejected(rejected: Readonly<Record<string, number>>): number {
  return Object.values(rejected).reduce((sum, count) => sum + count, 0);
}

/** A suggestion's parameters as `key = value` pairs, sorted, or a dash.
 *
 * Sorted so the same rule renders identically between refreshes — an unsorted object reorders on a
 * re-fetch and looks like a change to a suggestion that has not changed. The reference joins with
 * `; ` and falls back to an em dash, both transcribed.
 */
export function parameterText(parameters: Readonly<Record<string, string>>): string {
  const pairs = Object.entries(parameters)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key} = ${value}`);
  return pairs.length > 0 ? pairs.join('; ') : '—';
}

/** The eight lifecycle steps, and which two are marked.
 *
 * `here` is what pressing Create rule produces and `done` is where a rule ends up; the six between
 * them are the review nobody may skip, which is the panel's whole point. Numbered because this
 * genuinely is a sequence — the step a suggestion stops at is the screen's safety claim.
 */
export interface FlowStep {
  readonly n: string;
  readonly label: string;
  readonly mark: 'here' | 'done' | '';
}

export const FLOW: readonly FlowStep[] = [
  { n: '01', label: 'suggestions.flow.1', mark: '' },
  { n: '02', label: 'suggestions.flow.2', mark: 'here' },
  { n: '03', label: 'suggestions.flow.3', mark: '' },
  { n: '04', label: 'suggestions.flow.4', mark: '' },
  { n: '05', label: 'suggestions.flow.5', mark: '' },
  { n: '06', label: 'suggestions.flow.6', mark: '' },
  { n: '07', label: 'suggestions.flow.7', mark: '' },
  { n: '08', label: 'suggestions.flow.8', mark: 'done' },
];

export const PROVENANCE_PREFIX = 'suggested-from:';
/** The tag prefix a created rule carries. **Mirrors `SUGGESTION_TAG_PREFIX` on the server.**
 *
 * Two copies of a string is normally the two-vocabularies defect this codebase records at several
 * sites, and it is accepted here for one reason: the *server* mints the tag and puts it on
 * `RuleDraft.tags`, so a screen never composes one. This constant exists only to *read* a tag back
 * off a rule, which is a parse rather than a second authority — and if the two ever disagreed, the
 * failure is that a prefill does not offer to seed, never that a wrong rule is created.
 */

/** The suggestion a rule was created from, or `null` if it was authored by hand.
 *
 * **This is what makes the prefill survive a reload.** Creating a rule from a suggestion is two
 * calls — the columns and parameters live on the *version* — and router state does not cross a
 * refresh, so without a durable reference the second hop would arrive with the values gone.
 *
 * Returns the *first* match rather than refusing several: a rule with two provenance tags is not a
 * state this platform produces, and answering `null` there would drop the prefill for a rule that
 * plainly has one.
 */
export function suggestionIdFromTags(tags: readonly string[] | undefined): string | null {
  for (const tag of tags ?? []) {
    if (tag.startsWith(PROVENANCE_PREFIX)) {
      const id = tag.slice(PROVENANCE_PREFIX.length).trim();
      if (id !== '') return id;
    }
  }
  return null;
}

/** How many suggestions one dataset has. **`null` for a dataset the list does not name.**
 *
 * `null` rather than `0`, and the distinction is the contract's: a dataset with no suggestions is
 * absent from the answer, so a missing key means *nobody has generated for this dataset* while a
 * zero would claim a run happened and found nothing worth suggesting.
 */
export function suggestionCountFor(
  counts: readonly { readonly dataset_id: string; readonly suggestions: number }[],
  datasetId: string,
): number | null {
  const found = counts.find((entry) => entry.dataset_id === datasetId);
  return found === undefined ? null : found.suggestions;
}
